import React, { useCallback, useEffect, useState } from 'react';
import type {
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import { useSearchParams, useRouter } from 'next/navigation';

import { gql, useMutation, useQuery } from '@nerdwallet/apollo';

import { PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_TYPE } from '@constants/flows';
import {
  FinancialAccount,
  FinancialAccountSubtype,
  FinancialAccountType,
} from '@generated-gql/generated-types';
import PlaidLink from '@lib/utils/load-plaid-link';
import { useEmitRewardableEvent } from '@lib/hooks/nerdwallet-plus';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';
import { PlaidStep, useStepper } from './plaid-modal-stepper';
import { OAUTH_QUERY_PARAM, PLAID_OAUTH_REDIRECT } from '../constants';
import { removeCookie } from '@lib/utils/cookies';
import { GET_ACCOUNTS_FOR_USER_QUERY } from './queries';
import { datadogRum } from '@datadog/browser-rum';
import { useAnalytics } from '@lib/segment';
import { usePlaidModalVisibility } from './plaid-modal-visibility-context';

export const termsOfServiceAcknowledgementQuery = gql`
  query GetTermsOfServiceAcknowledgements {
    termsOfServiceIdentityProfile {
      acknowledgements {
        type
        source
        version
      }
    }
  }
`;

export const NOTIFY_PLAID_LINK_EVENT_MUTATION = gql`
  mutation NotifyPlaidLinkEvent(
    $event: ProxyEventType!
    $eventBody: JSON
    $financialPortalId: ID
    $isMigrate: Boolean
    $linkToken: String
    $marketplaceInstitutionId: ID
    $source: FinancialDataSource
    $sourceEntityId: ID
    $sourceInstitutionId: ID
  ) {
    publishFinancialDataLinkEvent(
      event: $event
      eventBody: $eventBody
      financialPortalId: $financialPortalId
      isMigrate: $isMigrate
      linkToken: $linkToken
      migrationMarketplaceInstitutionId: $marketplaceInstitutionId
      source: $source
      sourceEntityId: $sourceEntityId
      sourceInstitutionId: $sourceInstitutionId
    ) {
      success
    }
  }
`;

export const FdpLinkEvent = {
  AUTHENTICATION_SUCCESS: 'AUTHENTICATION_SUCCESS',
  EXIT: 'EXIT',
  FLOW_CLOSED: 'FLOW_CLOSED',
  FLOW_OPENED: 'FLOW_OPENED',
  SOURCE_EVENT: 'SOURCE_EVENT',
};

// Check if any financial account's institution was previously linked
const isNewInstitution = (
  institutionId: string,
  financialAccounts: FinancialAccount[],
) =>
  !financialAccounts?.some(
    (account) =>
      account?.financialPortal?.sourceInstitutionId === institutionId,
  );

export type NewPlaidLinkOnSuccess = (
  public_token: string,
  metadata: PlaidLinkOnSuccessMetadata,
  token: string,
) => void;

export const PlaidLinkLauncher: React.FC<{
  relinkProps?: {
    publicToken?: string;
    financialPortalId?: string;
    marketplaceInstitutionId?: string;
  };
  plaidModifiedLinkedAccounts: () => void;
}> = ({ relinkProps, plaidModifiedLinkedAccounts }) => {
  const { publicToken, financialPortalId, marketplaceInstitutionId } =
    relinkProps ?? {};
  const router = useRouter();
  const searchParams = useSearchParams();
  const { setShowPlaidVisibility } = usePlaidModalVisibility();

  const [isPlaidLinkOpen, setIsPlaidLinkOpen] = useState(false);
  const [emitGatedReward] = useEmitRewardableEvent();
  const { setRequiresPlaidTermsConsent } = useStepper();
  const { segmentTrack } = useAnalytics();
  const plaidMetadataRef = React.useRef<PlaidLinkOnSuccessMetadata | null>(
    null,
  );

  const [savePlaidAccount] = useMutation(NOTIFY_PLAID_LINK_EVENT_MUTATION, {
    onCompleted: () => {
      plaidModifiedLinkedAccounts();

      removeOAuthStateIdFromURL();
      removeCookie(PLAID_OAUTH_REDIRECT);

      const metadata = plaidMetadataRef.current;

      if (metadata) {
        const sourceInstitutionId = metadata?.institution?.institution_id;

        const isAllowedToPublishEvents = isNewInstitution(
          sourceInstitutionId,
          linkedAccounts?.financialAccounts?.accounts,
        );

        if (isAllowedToPublishEvents) {
          handleEvents(
            metadata.accounts,
            linkedAccounts?.financialAccounts?.accounts,
          );
        }
      }
    },
    onError: (error, options) => {
      removeOAuthStateIdFromURL();
      removeCookie(PLAID_OAUTH_REDIRECT);

      // Gracefully handle duplicate institutions linked error by closing the modal
      if (error.message.includes('duplicate institutions linked')) {
        datadogRum.addAction(
          'Plaid Modal - User attempted to link duplicate institution',
          error,
        );
        plaidModifiedLinkedAccounts();
      } else if (error.message.includes('invalid entity')) {
        const institutionName = options.variables.eventBody.institution.name;
        datadogRum.addAction(
          `Plaid Modal - User attempted to link invalid entity: ${institutionName}`,
          error,
        );
        setShowPlaidVisibility({
          show: true,
          plaidStep: PlaidStep.PLAID_MISSING_INSTITUTION_ERROR,
        });
      } else {
        setShowPlaidVisibility({
          show: true,
          plaidStep: PlaidStep.PLAID_ERROR,
        });
        datadogRum.addError(
          'Plaid Link Error - NOTIFY_PLAID_LINK_EVENT_MUTATION',
          error,
        );
      }
    },
    fetchPolicy: 'no-cache',
  });

  const { loading: isLoadingAcknowledgements, data: acknowledgementsData } =
    useQuery(termsOfServiceAcknowledgementQuery, {
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    // User must consent to Plaid terms before being allowed to link
    const checkTermsAcknowledgement = async () => {
      if (!isLoadingAcknowledgements) {
        const acknowledgements =
          acknowledgementsData?.termsOfServiceIdentityProfile?.acknowledgements;
        const requiresPlaidTermsConsent = !acknowledgements?.some(
          ({ type }) => type === PLAID_TERMS_OF_SERVICE_ACKNOWLEDGEMENT_TYPE,
        );

        if (requiresPlaidTermsConsent) {
          setRequiresPlaidTermsConsent(true);
          setShowPlaidVisibility({
            show: true,
            plaidStep: PlaidStep.PLAID_TERMS_CONSENT,
          });
        } else {
          setIsPlaidLinkOpen(true);
        }
      }
    };

    checkTermsAcknowledgement();
  }, [
    acknowledgementsData,
    isLoadingAcknowledgements,
    setShowPlaidVisibility,
    setRequiresPlaidTermsConsent,
  ]);

  const removeOAuthStateIdFromURL = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(OAUTH_QUERY_PARAM);
    router.replace(`?${params.toString()}`);
  }, [searchParams, router]);

  const handleEvents = useCallback(
    (accounts, linkedAccounts: FinancialAccount[]) => {
      const eventTypes = {
        credit: false,
        savings: false,
        investment: false,
        loan: false,
      };

      if (accounts.some((account) => account.type === 'credit')) {
        emitGatedReward(RewardableEventType.LINKED_CREDIT_CARD_ACCOUNT);
        eventTypes.credit = true;
      }

      if (
        accounts.some(
          (account) =>
            account.type === 'depository' && account.subtype === 'savings',
        )
      ) {
        if (!eventTypes.savings) {
          emitGatedReward(RewardableEventType.LINKED_SAVINGS_ACCOUNT);
          eventTypes.savings = true;
        }
      }

      if (accounts.some((account) => account.type === 'investment')) {
        if (!eventTypes.investment) {
          emitGatedReward(RewardableEventType.LINKED_INVESTMENT_ACCOUNT);
          eventTypes.investment = true;
        }
      }

      if (accounts.some((account) => account.type === 'loan')) {
        if (!eventTypes.loan) {
          emitGatedReward(RewardableEventType.LINKED_LOAN_ACCOUNT);
          eventTypes.loan = true;
        }
      }

      if (
        eventTypes.credit &&
        eventTypes.loan &&
        eventTypes.investment &&
        eventTypes.savings
      ) {
        emitGatedReward(RewardableEventType.LINKED_ALL_ACCOUNT_TYPES);
      } else {
        linkedAccounts?.forEach((currentValue) => {
          switch (currentValue?.type) {
            case FinancialAccountType.CreditCard: {
              if (!eventTypes.credit) {
                eventTypes.credit = true;
              }
              break;
            }
            case FinancialAccountType.Bank: {
              if (currentValue?.subtype === FinancialAccountSubtype.Savings) {
                if (!eventTypes.savings) {
                  eventTypes.savings = true;
                }
              }
              break;
            }
            case FinancialAccountType.Investment: {
              if (!eventTypes.investment) {
                eventTypes.investment = true;
              }
              break;
            }
            case FinancialAccountType.Loan: {
              if (!eventTypes.loan) {
                eventTypes.loan = true;
              }
              break;
            }
            default:
              break;
          }
        });
        if (
          eventTypes.credit &&
          eventTypes.savings &&
          eventTypes.investment &&
          eventTypes.loan
        ) {
          emitGatedReward(RewardableEventType.LINKED_ALL_ACCOUNT_TYPES);
        }
      }
    },
    [emitGatedReward],
  );

  const { data: linkedAccounts } = useQuery(GET_ACCOUNTS_FOR_USER_QUERY, {
    fetchPolicy: 'cache-first',
  });

  const onSuccess = useCallback<NewPlaidLinkOnSuccess>(
    async (public_token, metadata, token) => {
      const sourceInstitutionId = metadata?.institution?.institution_id;
      const variables = {
        event: FdpLinkEvent.AUTHENTICATION_SUCCESS,
        eventBody: {
          ...metadata,
          products: ['transactions'],
        },
        financialPortalId: financialPortalId,
        isMigrate: false,
        marketplaceInstitutionId: marketplaceInstitutionId,
        source: 'PLAID',
        sourceEntityId: undefined,
        linkToken: token,
        sourceInstitutionId,
      };

      plaidMetadataRef.current = metadata;

      await savePlaidAccount({
        variables,
      });
    },
    [savePlaidAccount, financialPortalId, marketplaceInstitutionId],
  );

  const onEvent = useCallback<PlaidLinkOnEvent>(
    (eventName, metadata) => {
      segmentTrack('Plaid Financial Account Linking Activity Occurred', {
        event_name: eventName,
        ...metadata,
        source: 'PLAID',
        sourceInstitutionId: metadata?.institution_id,
      });
    },
    [segmentTrack],
  );

  const onExit = useCallback<PlaidLinkOnExit>(
    (error, metadata) => {
      plaidModifiedLinkedAccounts();

      segmentTrack('Plaid Financial Account Linking Activity Occurred', {
        ...metadata,
        ...error,
        products: ['transactions'],
      });
    },
    [plaidModifiedLinkedAccounts, segmentTrack],
  );

  if (!isPlaidLinkOpen) {
    return null;
  }

  return (
    <PlaidLink
      plaidLinkOnSuccess={onSuccess}
      plaidLinkOnEvent={onEvent}
      plaidLinkOnExit={onExit}
      relinkPublicToken={publicToken}
    />
  );
};

export default PlaidLinkLauncher;
